import ReactMarkdown from "react-markdown";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkDirective from "remark-directive";
import remarkGfm from "remark-gfm";

import { cn } from "@/client/lib/utils";

/**
 * Render trusted (staff-authored) Outline markdown. Content is still passed
 * through rehype-sanitize as defense-in-depth so a malformed/hostile document
 * can't inject script. Element styling is applied via descendant utility
 * classes on the wrapper (the project doesn't ship the Tailwind typography
 * plugin), which also avoids forwarding react-markdown's internal `node` prop
 * onto DOM elements.
 */

// Outline callout/notice blocks are authored as `:::info`, `:::tip`,
// `:::warning`, `:::success` directive containers. remark-gfm doesn't parse
// directives, so without this they render as literal `:::` text (F1). Map the
// known kinds to a styled div; unknown kinds fall back to a neutral note so a
// stray directive never leaks raw markers.
const CALLOUT_KINDS = new Set(["info", "tip", "warning", "success", "note", "danger", "caution"]);

interface MdNode {
  type: string;
  name?: string;
  data?: { hName?: string; hProperties?: Record<string, unknown> };
  children?: MdNode[];
}

function remarkCallouts() {
  return (tree: MdNode) => {
    const walk = (node: MdNode) => {
      if (!node.children) {
        return;
      }
      for (const child of node.children) {
        if ((child.type === "containerDirective" || child.type === "leafDirective") && child.name) {
          const kind = CALLOUT_KINDS.has(child.name) ? child.name : "note";
          child.data = child.data ?? {};
          child.data.hName = "div";
          child.data.hProperties = { className: ["callout", `callout-${kind}`] };
        }
        walk(child);
      }
    };
    walk(tree);
  };
}

// Allow only the callout class names on div, so the sanitizer keeps our styling
// hooks while still stripping arbitrary author-supplied classes/scripts.
const calloutClasses = ["callout", ...[...CALLOUT_KINDS].map((k) => `callout-${k}`)];
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    div: [...(defaultSchema.attributes?.div ?? []), ["className", ...calloutClasses]],
  },
};

const proseClasses = cn(
  "text-sm leading-7",
  "[&_h1]:mt-8 [&_h1]:mb-3 [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:tracking-tight [&_h1:first-child]:mt-0",
  "[&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2:first-child]:mt-0",
  "[&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold",
  "[&_h4]:mt-4 [&_h4]:mb-2 [&_h4]:font-semibold",
  "[&_p]:my-3",
  "[&_a]:text-primary [&_a]:font-medium [&_a]:underline [&_a]:underline-offset-4",
  "[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1",
  "[&_blockquote]:border-muted [&_blockquote]:text-muted-foreground [&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:pl-4 [&_blockquote]:italic",
  "[&_hr]:my-6",
  "[&_img]:my-4 [&_img]:max-w-full [&_img]:rounded-md [&_img]:border",
  "[&_pre]:bg-muted [&_pre]:my-4 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:p-4 [&_pre]:text-sm",
  "[&_code]:bg-muted [&_code]:rounded [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-sm",
  "[&_pre_code]:bg-transparent [&_pre_code]:p-0",
  "[&_table]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm",
  "[&_th]:border-b [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-medium",
  "[&_td]:border-b [&_td]:px-3 [&_td]:py-2 [&_td]:align-top",
  // Callouts (Outline notice blocks).
  "[&_.callout]:my-4 [&_.callout]:rounded-md [&_.callout]:border [&_.callout]:p-3",
  "[&_.callout>:first-child]:mt-0 [&_.callout>:last-child]:mb-0",
  "[&_.callout-info]:border-[var(--info-solid)] [&_.callout-info]:bg-[var(--info-bg)]",
  "[&_.callout-note]:border-border [&_.callout-note]:bg-muted/40",
  "[&_.callout-tip]:border-[var(--success-solid)] [&_.callout-tip]:bg-[var(--success-bg)]",
  "[&_.callout-success]:border-[var(--success-solid)] [&_.callout-success]:bg-[var(--success-bg)]",
  "[&_.callout-warning]:border-[var(--warning-solid)] [&_.callout-warning]:bg-[var(--warning-bg)]",
  "[&_.callout-caution]:border-[var(--warning-solid)] [&_.callout-caution]:bg-[var(--warning-bg)]",
  "[&_.callout-danger]:border-[var(--danger-solid)] [&_.callout-danger]:bg-[var(--danger-bg)]",
);

export function Markdown({ content }: { content: string }) {
  return (
    <div className={proseClasses}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkDirective, remarkCallouts]}
        rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
